import { useMemo, useReducer } from 'react';
import { withAttachedFactorConstraint } from '../utils/optimizerConstraintEditor';
import { OPTIMIZER_TRADING_RULES } from '../utils/optimizerGenericGroupContract';
import { filterGroupConstraintsToCatalog } from '../utils/optimizerRunCommand';
import {
    DEFAULT_INFORMATION_COEFFICIENT,
    MANUAL_OBJECTIVES,
    MAX_SHARPE_OBJECTIVE,
    manualDesign,
    tradeIdeaExpectedReturnPolicy,
} from '../utils/optimizerObjectives';

const initialConstraints = Object.freeze({
    static_constraints: {
        nmv_pct_gmv: 5,
        aum: null,
        gmv: null,
        position_gmv_pct_of_aum: 15,
        beta_exp_as_pct_of_aum: 10,
        // Factor volatility of 3.5% under the 5% total cap keeps factors to at
        // most about half of total variance (3.5² / 5² ≈ 49%).
        factor_risk_pct_of_total: 3.5,
        port_vol_pct_aum: 5,
        ticker_gmv_pct_delta: 50,
        max_names_changed: 10,
        ...Object.fromEntries(OPTIMIZER_TRADING_RULES.map(
            ({ field, defaultValue }) => [field, defaultValue],
        )),
    },
    dynamic_constraints: {},
});

const initialConstraintToggles = Object.freeze({
    static_constraints: {
        nmv_pct_gmv: true,
        aum: true,
        gmv: true,
        position_gmv_pct_of_aum: true,
        beta_exp_as_pct_of_aum: true,
        factor_risk_pct_of_total: true,
        port_vol_pct_aum: true,
        ticker_gmv_pct_delta: true,
        max_names_changed: false,
        ...Object.fromEntries(OPTIMIZER_TRADING_RULES.map(({ field }) => [field, false])),
    },
});

export function buildOptimizerDefaultConstraints(aum, gmv = null) {
    return {
        static_constraints: {
            ...initialConstraints.static_constraints,
            aum: aum ?? null,
            gmv,
        },
        dynamic_constraints: {},
    };
}

function defaultEditorState(aum, gmv = null) {
    return {
        constraints: buildOptimizerDefaultConstraints(aum, gmv),
        constraintToggles: { static_constraints: { ...initialConstraintToggles.static_constraints } },
        groupConstraints: [],
        objective: 'AlphaScore',
        gmvBudgetMode: 'auto',
    };
}

const resolveUpdate = (valueOrUpdater, previous) => (
    typeof valueOrUpdater === 'function' ? valueOrUpdater(previous) : valueOrUpdater
);
const withConstraints = (state, constraints) => (
    Object.is(state.constraints, constraints) ? state : { ...state, constraints }
);
const withGroups = (state, groupConstraints) => (
    Object.is(state.groupConstraints, groupConstraints) ? state : { ...state, groupConstraints }
);

// This reducer owns only editable configuration. Receipt invalidation, baseline
// hydration and request fences remain with the workspace that dispatches edits.
export function optimizerEditorReducer(state, event) {
    switch (event.type) {
        case 'constraints-edited':
            return withConstraints(state, resolveUpdate(event.update, state.constraints));
        case 'constraint-toggles-edited': {
            const constraintToggles = resolveUpdate(event.update, state.constraintToggles);
            return Object.is(state.constraintToggles, constraintToggles) ? state : { ...state, constraintToggles };
        }
        case 'group-constraints-edited':
            return withGroups(state, resolveUpdate(event.update, state.groupConstraints));
        case 'objective-chosen': {
            let constraints = state.constraints;
            const nextObjective = event.objective;
            if (nextObjective === MAX_SHARPE_OBJECTIVE) {
                // Max Sharpe has no construction balance; it states its skill instead.
                constraints = {
                    ...constraints,
                    expected_return_policy: constraints.expected_return_policy
                        ?? tradeIdeaExpectedReturnPolicy(event.rememberedSkill ?? DEFAULT_INFORMATION_COEFFICIENT),
                };
                delete constraints.design;
            } else if (nextObjective in MANUAL_OBJECTIVES) {
                constraints = {
                    ...constraints,
                    ...(nextObjective !== 'AlphaScore' || constraints.design
                        ? { design: manualDesign(nextObjective, constraints.design) } : {}),
                };
                delete constraints.expected_return_policy;
            }
            return Object.is(constraints, state.constraints) && Object.is(nextObjective, state.objective)
                ? state : { ...state, constraints, objective: nextObjective };
        }
        case 'user-reset':
            return defaultEditorState(event.aum);
        case 'defaults-restored':
            return defaultEditorState(event.aum, event.gmv);
        case 'configuration-installed':
            // Recorded settings already passed their receipt contract. Install
            // them exactly, without applying user objective-switch defaults.
            return {
                constraints: event.configuration.constraints,
                constraintToggles: event.configuration.constraintToggles,
                groupConstraints: event.configuration.groupConstraints,
                objective: event.configuration.objective,
                gmvBudgetMode: 'manual',
            };
        case 'aum-hydrated':
            return withConstraints(state, {
                ...state.constraints,
                static_constraints: { ...state.constraints.static_constraints, aum: event.aum ?? null },
            });
        case 'automatic-gmv-synchronized':
            return state.constraints.static_constraints?.gmv === event.gmv ? state : withConstraints(state, {
                ...state.constraints,
                static_constraints: { ...state.constraints.static_constraints, gmv: event.gmv },
            });
        case 'checked-gmv-followed':
            return state.gmvBudgetMode === 'auto' ? state : { ...state, gmvBudgetMode: 'auto' };
        case 'gmv-marked-manual':
            return state.gmvBudgetMode === 'manual' ? state : { ...state, gmvBudgetMode: 'manual' };
        case 'groups-reconciled':
            return withGroups(state, filterGroupConstraintsToCatalog(state.groupConstraints, event.catalog));
        case 'groups-cleared':
            return { ...state, groupConstraints: [] };
        case 'factor-attached':
            return withConstraints(state, withAttachedFactorConstraint(state.constraints, event.factor, event.riskType));
        case 'factor-removed':
            return withConstraints(state, {
                ...state.constraints,
                dynamic_constraints: Object.fromEntries(
                    Object.entries(state.constraints.dynamic_constraints || {}).filter(([key]) => key !== event.key),
                ),
            });
        default:
            throw new Error(`Unknown Optimizer editor transition: ${event.type}`);
    }
}

export default function useOptimizerEditorState(aum) {
    const [state, dispatch] = useReducer(optimizerEditorReducer, aum, defaultEditorState);
    const actions = useMemo(() => ({
        setConstraints: (update) => dispatch({ type: 'constraints-edited', update }),
        setConstraintToggles: (update) => dispatch({ type: 'constraint-toggles-edited', update }),
        setGroupConstraints: (update) => dispatch({ type: 'group-constraints-edited', update }),
        chooseObjective: (objective, rememberedSkill = null) => dispatch({ type: 'objective-chosen', objective, rememberedSkill }),
        resetForUser: (nextAum) => dispatch({ type: 'user-reset', aum: nextAum }),
        resetDefaults: ({ aum: nextAum, gmv }) => dispatch({ type: 'defaults-restored', aum: nextAum, gmv }),
        installConfiguration: (configuration) => dispatch({ type: 'configuration-installed', configuration }),
        hydrateAum: (nextAum) => dispatch({ type: 'aum-hydrated', aum: nextAum }),
        syncAutomaticGmv: (gmv) => dispatch({ type: 'automatic-gmv-synchronized', gmv }),
        followCheckedGmv: () => dispatch({ type: 'checked-gmv-followed' }),
        markGmvManual: () => dispatch({ type: 'gmv-marked-manual' }),
        reconcileGroups: (catalog) => dispatch({ type: 'groups-reconciled', catalog }),
        clearGroups: () => dispatch({ type: 'groups-cleared' }),
        attachFactor: (factor, riskType) => dispatch({ type: 'factor-attached', factor, riskType }),
        removeFactor: (key) => dispatch({ type: 'factor-removed', key }),
    }), [dispatch]);
    return { state, actions };
}
